import { TextField } from "@fluentui/react";
import {
  Label,
  makeStyles,
  MessageBar,
  Option,
  Switch,
} from "@fluentui/react-components";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProcessFieldUniqueName } from "../constants/Fields";
import {
  collectionTitlesTagCollectionId,
  processTagCollectionId,
  variantsTagCollectionId,
} from "../constants/TagCollections";
import { useGetFilterableFields } from "../hooks/useSearchConfiguration";
import { useUnsavedChanges } from "../hooks/useUnsavedChanges";
import {
  Chapter,
  ChapterFormData,
  ChapterNavigationConfig,
} from "../models/Chapter";
import { useChaptersGetAll } from "../hooks/useChapter";
import { SearchFilterConfiguration } from "../models/SearchConfiguration";
import { VsaaTag } from "../models/VsaaTag";
import { SkanskaColorTokens } from "../style/colors";
import { ButtonWithLoading } from "./ButtonWithLoading";
import { ChapterDeleteButton } from "./ChapterDeleteButton";
import { ClearableDropdown } from "./ClearableDropdown";
import ImagePicker from "./image/ImagePicker";
import { SearchConfigurationPicker } from "./search/SearchConfigurationPicker";
import { VsaaTagPicker } from "./tagpicker/VsaaTagPicker";

interface ActivityFormProps {
  existingChapter?: Chapter | undefined;
  onSubmit: (data: ChapterFormData) => void;
  isLoading: boolean;
  isRefetching: boolean;
}

const useStyles = makeStyles({
  root: {
    gap: "10px",
    display: "flex",
    flexDirection: "column",
  },
  formContainer: {
    display: "flex",
  },
  preview: {
    padding: "16px",
    borderRadius: "20px",
    height: "100%",
    border: `2px solid ${SkanskaColorTokens.color_secondary_dim_black}`,
  },
  actionButtons: {
    marginTop: "20px",
    justifyContent: "space-between",
    display: "flex",
  },
  editArea: {
    minWidth: "500px",
    margin: "25px 0",
  },
  formSpacingContainer: {
    margin: "10px 0",
  },
  fieldLabel: {
    fontWeight: 600,
  },
});

const ChapterForm = (props: ActivityFormProps) => {
  const { existingChapter, onSubmit, isLoading, isRefetching } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [navigationHasChanges, setNavigationHasChanges] = useState(false);
  const { data: fields } = useGetFilterableFields();
  const { data: chapters } = useChaptersGetAll();
  const [formData, setFormData] = useState<ChapterFormData>({
    title: existingChapter ? existingChapter.title : "",
    collectionTitle: existingChapter
      ? existingChapter.collectionTitle
      : undefined,
    KMACollaborationVariant: existingChapter
      ? existingChapter.KMACollaborationVariant
      : undefined,
    rootNode: existingChapter ? existingChapter.rootProcessNode : undefined,
    imageId: existingChapter ? existingChapter.imageId : "",
    searchFilterConfiguration:
      existingChapter && existingChapter.searchFilterConfiguration
        ? existingChapter.searchFilterConfiguration
        : { enableFilterOnChapters: false, filters: [] },
    chapterNavigationConfig:
      existingChapter && existingChapter.chapterNavigationConfig
        ? existingChapter.chapterNavigationConfig
        : {
            enableTableNavigation: false,
            navigationTableColumnTagId: undefined,
            navigationTableRowTagId: undefined,
          },
  });

  const usedKmaVariantTagIds = useMemo<string[]>(() => {
    if (!chapters) {
      return [];
    }

    return chapters
      .filter((chapter) => chapter.id !== existingChapter?.id)
      .map((chapter) => chapter.KMACollaborationVariant?.id)
      .filter((tagId): tagId is string => Boolean(tagId));
  }, [chapters, existingChapter?.id]);

  const isKmaVariantValidationError = useMemo<boolean>(() => {
    const selectedVariantId = formData.KMACollaborationVariant?.id;
    return Boolean(
      selectedVariantId && usedKmaVariantTagIds.includes(selectedVariantId)
    );
  }, [formData.KMACollaborationVariant?.id, usedKmaVariantTagIds]);

  const isNavigationProcessValidationError = useMemo<boolean>(() => {
    if (!formData.chapterNavigationConfig.enableTableNavigation) {
      return false;
    }

    const columnField = fields?.find(
      (f) =>
        f.tagCollectionId ===
        formData.chapterNavigationConfig.navigationTableColumnTagId
    );
    const rowField = fields?.find(
      (f) =>
        f.tagCollectionId ===
        formData.chapterNavigationConfig.navigationTableRowTagId
    );

    const hasProcessInColumn =
      columnField?.uniqueName === ProcessFieldUniqueName;
    const hasProcessInRow = rowField?.uniqueName === ProcessFieldUniqueName;

    return !hasProcessInColumn && !hasProcessInRow;
  }, [formData.chapterNavigationConfig, fields]);

  const isColumnDropdownEmpty = useMemo<boolean>(() => {
    return (
      formData.chapterNavigationConfig.enableTableNavigation &&
      !formData.chapterNavigationConfig.navigationTableColumnTagId
    );
  }, [formData.chapterNavigationConfig]);

  const submitEnabled = useMemo<boolean>(() => {
    // We don't allow empty values. TODO: add imageId as required field
    if (!formData.title || !formData.collectionTitle || !formData.rootNode) {
      return false;
    }

    if (isNavigationProcessValidationError) {
      return false;
    }

    if (isKmaVariantValidationError) {
      return false;
    }

    // When table navigation is enabled, navigationTableColumnTagId is mandatory
    if (
      formData.chapterNavigationConfig.enableTableNavigation &&
      !formData.chapterNavigationConfig.navigationTableColumnTagId
    ) {
      return false;
    }

    const filtersHasChanges = (): boolean => {
      if (!existingChapter?.searchFilterConfiguration) {
        return true;
      }

      if (
        existingChapter.searchFilterConfiguration.filters.length !==
        formData.searchFilterConfiguration.filters.length
      ) {
        return true;
      }

      let hasChange = false;
      formData.searchFilterConfiguration.filters.forEach((filter, index) => {
        const existingFilter =
          existingChapter.searchFilterConfiguration?.filters[index];

        if (!existingFilter) {
          hasChange = true;
          return;
        }

        if (filter.fieldUniqueName !== existingFilter.fieldUniqueName) {
          hasChange = true;
          return;
        }

        if (filter.values.length !== existingFilter.values.length) {
          hasChange = true;
          return;
        }

        filter.values.forEach((filterValue, valueIndex) => {
          const existingFilterValue = existingFilter.values[valueIndex];

          if (filterValue.value !== existingFilterValue.value) {
            hasChange = true;
            return;
          }
        });

        if (hasChange) {
          return;
        }
      });

      return hasChange;
    };

    // No need to update if there are no changes.
    if (
      formData.title === existingChapter?.title &&
      formData.collectionTitle.id === existingChapter?.collectionTitle.id &&
      (formData.KMACollaborationVariant?.id ?? "") ===
        (existingChapter?.KMACollaborationVariant?.id ?? "") &&
      formData.rootNode.id === existingChapter.rootProcessNode?.id &&
      formData.imageId === existingChapter.imageId &&
      !filtersHasChanges() &&
      !navigationHasChanges
    ) {
      return false;
    }

    return true;
  }, [
    formData,
    existingChapter,
    isNavigationProcessValidationError,
    isKmaVariantValidationError,
    navigationHasChanges,
  ]);

  const classes = useStyles();
  const { t } = useTranslation();
  const handleChange = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ): void => {
    const name =
      event.currentTarget.name || event.currentTarget.ariaLabel || "";
    if (name) {
      setFormData((formData) => ({
        ...formData,
        [name]: newValue !== undefined ? newValue : event.currentTarget.value,
      }));
    }
  };

  const handleSubmit = (
    event?: React.FormEvent<HTMLFormElement> | unknown
  ): void => {
    if (event && typeof event === "object" && "preventDefault" in event) {
      (event as React.FormEvent<HTMLFormElement>).preventDefault();
    }

    if (!submitEnabled) {
      return;
    }
    setIsSubmitting(true);
    onSubmit(formData);
    setNavigationHasChanges(false);
  };

  const handleCollectionTitleChange = (value: VsaaTag[]) => {
    setFormData((formData) => ({
      ...formData,
      collectionTitle: value[0] ?? undefined,
    }));
  };

  const handleKMACollaborationVariantChange = (value: VsaaTag[]) => {
    setFormData((formData) => ({
      ...formData,
      KMACollaborationVariant: value[0] ?? undefined,
    }));
  };

  const handleRootNodeChange = (value: VsaaTag[]) => {
    setFormData((formData) => ({
      ...formData,
      rootNode: value[0] ?? undefined,
    }));
  };

  const handleThumbnailChange = (value: string) => {
    setFormData((formData) => ({
      ...formData,
      ["imageId"]: value ?? undefined,
    }));
  };

  const handleSearchConfigurationChange = (
    newConfig: SearchFilterConfiguration
  ) => {
    setFormData((formData) => ({
      ...formData,
      ["searchFilterConfiguration"]: newConfig ?? {
        enableFilterOnChapters: false,
        filters: [],
      },
    }));
  };

  const handleNavigationConfigChange = (newConfig: ChapterNavigationConfig) => {
    setNavigationHasChanges(true);
    setFormData((formData) => ({
      ...formData,
      ["chapterNavigationConfig"]: newConfig ?? {
        enableTableNavigation: false,
        navigationTableColumnTagId: undefined,
        navigationTableRowTagId: undefined,
      },
    }));
  };

  useUnsavedChanges(submitEnabled && !isSubmitting, t("unsavedChangesMessage"));

  return (
    <div className={classes.root}>
      <div className={classes.formContainer}>
        <div className={classes.editArea}>
          <form onSubmit={handleSubmit}>
            <TextField
              label={t("title")}
              name="title"
              value={formData.title}
              onChange={handleChange}
            />
            <div className={classes.formSpacingContainer}>
              <VsaaTagPicker
                tagCollectionId={collectionTitlesTagCollectionId}
                label={t("collectionTitle")}
                onChange={handleCollectionTitleChange}
                value={
                  !formData.collectionTitle
                    ? undefined
                    : [formData.collectionTitle]
                }
              />
            </div>
            <div className={classes.formSpacingContainer}>
              <VsaaTagPicker
                tagCollectionId={processTagCollectionId}
                label={t("navigationNode")}
                onChange={handleRootNodeChange}
                value={!formData.rootNode ? undefined : [formData.rootNode]}
              />
            </div>
            <div className={classes.formSpacingContainer}>
              <VsaaTagPicker
                tagCollectionId={variantsTagCollectionId}
                label={t("variant")}
                onChange={handleKMACollaborationVariantChange}
                usedTagsId={usedKmaVariantTagIds}
                usedTagLabel={t("editChapterForm.kmaVariantInUse")}
                value={
                  !formData.KMACollaborationVariant
                    ? undefined
                    : [formData.KMACollaborationVariant]
                }
              />
              {isKmaVariantValidationError && (
                <MessageBar intent="error" style={{ marginTop: "8px" }}>
                  {t("editChapterForm.kmaVariantAlreadyUsed")}
                </MessageBar>
              )}
            </div>
            <div className={classes.formSpacingContainer}>
              <ImagePicker
                onImageSelect={handleThumbnailChange}
                selectedImageId={formData.imageId}
              />
            </div>
            <div className={classes.formSpacingContainer}>
              <SearchConfigurationPicker
                configuration={formData.searchFilterConfiguration}
                onConfigurationChange={handleSearchConfigurationChange}
                dropdownLabel={t("editChapterForm.chapterFilters")}
                dropdownHint={t("editChapterForm.chapterFilterHint")}
              />
            </div>
            <div className={classes.formSpacingContainer}>
              <Label className={classes.fieldLabel}>
                {t("editChapterForm.chapterNavigationConfig")}
              </Label>
              <div style={{ marginTop: "8px" }}>
                <Switch
                  label={t("editChapterForm.enableTableNavigation")}
                  checked={
                    formData.chapterNavigationConfig.enableTableNavigation
                  }
                  onChange={(_, data) => {
                    handleNavigationConfigChange({
                      ...formData.chapterNavigationConfig,
                      enableTableNavigation: data.checked,
                    });
                  }}
                />
              </div>
              {formData.chapterNavigationConfig.enableTableNavigation && (
                <div style={{ marginTop: "16px", paddingLeft: "16px" }}>
                  <div style={{ marginBottom: "8px" }}>
                    <ClearableDropdown
                      label={
                        <span className={classes.fieldLabel}>
                          {t("editChapterForm.navigationTableColumnTagId")}
                        </span>
                      }
                      value={(() => {
                        const storedValue =
                          formData.chapterNavigationConfig
                            .navigationTableColumnTagId;
                        if (!storedValue) return "";

                        const fieldByTagCollection = fields?.find(
                          (f) => f.tagCollectionId === storedValue
                        );
                        return fieldByTagCollection
                          ? fieldByTagCollection.displayName
                          : "";
                      })()}
                      selectedOptions={(() => {
                        const storedValue =
                          formData.chapterNavigationConfig
                            .navigationTableColumnTagId;
                        if (!storedValue) return [];

                        const fieldByTagCollection = fields?.find(
                          (f) => f.tagCollectionId === storedValue
                        );
                        return fieldByTagCollection
                          ? [fieldByTagCollection.uniqueName]
                          : [];
                      })()}
                      onOptionSelect={(_, data) => {
                        const selectedField = fields?.find(
                          (f) => f.uniqueName === data.optionValue
                        );
                        handleNavigationConfigChange({
                          ...formData.chapterNavigationConfig,
                          navigationTableColumnTagId:
                            selectedField?.tagCollectionId || undefined,
                        });
                      }}
                      onClear={() => {
                        handleNavigationConfigChange({
                          ...formData.chapterNavigationConfig,
                          navigationTableColumnTagId: undefined,
                        });
                      }}
                    >
                      {fields
                        ?.filter((f) => f.tagCollectionId)
                        .map((field) => (
                          <Option
                            key={field.uniqueName}
                            value={field.uniqueName}
                          >
                            {field.displayName}
                          </Option>
                        ))}
                    </ClearableDropdown>
                  </div>
                  <div>
                    <ClearableDropdown
                      label={
                        <span className={classes.fieldLabel}>
                          {t("editChapterForm.navigationTableRowTagId")}
                        </span>
                      }
                      value={(() => {
                        const storedValue =
                          formData.chapterNavigationConfig
                            .navigationTableRowTagId;
                        if (!storedValue) return "";
                        const fieldByTagCollection = fields?.find(
                          (f) => f.tagCollectionId === storedValue
                        );
                        return fieldByTagCollection
                          ? fieldByTagCollection.displayName
                          : "";
                      })()}
                      selectedOptions={(() => {
                        const storedValue =
                          formData.chapterNavigationConfig
                            .navigationTableRowTagId;
                        if (!storedValue) return [];
                        const fieldByTagCollection = fields?.find(
                          (f) => f.tagCollectionId === storedValue
                        );
                        return fieldByTagCollection
                          ? [fieldByTagCollection.uniqueName]
                          : [];
                      })()}
                      onOptionSelect={(_, data) => {
                        const selectedField = fields?.find(
                          (f) => f.uniqueName === data.optionValue
                        );
                        handleNavigationConfigChange({
                          ...formData.chapterNavigationConfig,
                          navigationTableRowTagId:
                            selectedField?.tagCollectionId || undefined,
                        });
                      }}
                      onClear={() => {
                        handleNavigationConfigChange({
                          ...formData.chapterNavigationConfig,
                          navigationTableRowTagId: undefined,
                        });
                      }}
                    >
                      {fields
                        ?.filter((f) => f.tagCollectionId)
                        .map((field) => (
                          <Option
                            key={field.uniqueName}
                            value={field.uniqueName}
                          >
                            {field.displayName}
                          </Option>
                        ))}
                    </ClearableDropdown>
                  </div>
                  {isNavigationProcessValidationError && (
                    <MessageBar intent="error" style={{ marginTop: "8px" }}>
                      {t("navigationProcessValidation")}
                    </MessageBar>
                  )}
                  {isColumnDropdownEmpty && (
                    <MessageBar intent="error" style={{ marginTop: "8px" }}>
                      {t("navigationColumnRequired")}
                    </MessageBar>
                  )}
                </div>
              )}
            </div>
            <div className={classes.actionButtons}>
              <ButtonWithLoading
                text={existingChapter ? t("saveChapter") : t("createChapter")}
                onClick={handleSubmit}
                disabled={!submitEnabled}
                isLoading={isLoading || isRefetching}
                width={"200px"}
              />

              {existingChapter && (
                <ChapterDeleteButton Chapter={existingChapter} />
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChapterForm;
